import {
	type FrontendPlacementMessage,
	type AnyFrontendStage,
} from '@post-office/message-maker-browser/types';

import { useMemo } from 'react';

import { type NextBestAction } from '@post-office/confluence-next-best-action';

import { PlacementFrontend } from '@post-office/browser--growth/confluence-side-nav';

import { type MessageTemplateContext } from '../../../index';

import { assertMessageTemplateContext } from '../../../__generated__/validation/messageTemplateContext';

import type { CreatePlacementMessageConfig } from '@post-office/message-template-contracts/placement-v2';

import { Connect3pTools, type ThirdPartyApp } from './components/connect-3p-tools';

import type { AnimationApp, Department } from './components/connect-3p-tools-modal';

import {
	ANIMATION_APP_KEYS,
	DEPARTMENT_KEYS,
} from '@atlassian/confluence-account-setup/inline-connection-screen/get-animation-svg';

const ANIMATION_APPS = new Set<ThirdPartyApp>(ANIMATION_APP_KEYS);

const DEPARTMENTS = new Set<string>(DEPARTMENT_KEYS);

type MessageFrontendProps = MessageTemplateContext & {
	handleOnDismissed: () => void;
	onSideNavClick: (nextBestActionType: NextBestAction) => string | void | null;
};

export const MessageFrontend: (config: CreatePlacementMessageConfig) => FrontendPlacementMessage<{
	render: AnyFrontendStage;
}> = PlacementFrontend.createMessageFrontend({
	render: {
		validate: assertMessageTemplateContext,
		Component: ({
			nextBestActionType,
			cohort,
			cloudId,
			userId,
			unconnectedApps,
			topApps,
			department,
			onSideNavClick,
			handleOnDismissed,
		}: MessageFrontendProps) => {
			const narrowedTopApps = useMemo(
				() => topApps.filter((app): app is AnimationApp => ANIMATION_APPS.has(app)),
				[topApps],
			);
			const narrowedDepartment = useMemo(
				() => (DEPARTMENTS.has(department) ? (department as Department) : undefined),
				[department],
			);
			// `unconnectedApps` is populated server-side by the implicit-message-creator
			// via a 3-tier cascade (team-type TAP trait → site-wide TAP trait → hardcoded
			// defaults). On total creator failure the message is suppressed entirely
			// (see implicit-message/index.ts outer catch), so this prop is always
			// non-empty in production.
			// `cloudId` and `userId` are required and used by Connect3pTools to call
			// `use3pConnectorResources` and suppress the nudge when the user is
			// already connected to enough of the recommended apps.
			return (
				<Connect3pTools
					action={nextBestActionType}
					cohort={cohort}
					cloudId={cloudId}
					userId={userId}
					unconnectedApps={unconnectedApps}
					topApps={narrowedTopApps}
					department={narrowedDepartment}
					onPrimaryActionClick={onSideNavClick}
					onDismissClick={handleOnDismissed}
				/>
			);
		},
	},
});
